import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from 'react-router-dom';
import { useNavigationStatus } from '../hooks/useNavigationStatus';
import { useFocusTrap } from "../hooks/useFocusTrap";
import { useDropdownClose } from "../hooks/useDropdownClose";
import { API_BASE, authHeaders } from "../constants/api";
import { entityGroupError } from "../utils/thingsboardEdition";
import Pagination from "./Pagination";
import { useAuth } from "../context/AuthContext";
import "./DeviceGroups.css";
import { Sortieren, Suche, Öffnen, Löschen, Abbrechen, Hinzufügen, Hinweis, Bestätigen } from "../icons";
import { generateSuggestions } from '../utils/suggestions';
import { formatDateDE } from '../utils/formatDate';

const PAGE_SIZE = 10;

function DashboardGroups() {
  const [groups, setGroups] = useState([]);
  const [groupCount, setGroupCount] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  /* Suche */
  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedSuggestion, setHighlightedSuggestion] = useState(-1);
  const searchInputRef = useRef(null);

  /* Sortierung */
  const [sortOption, setSortOption] = useState("name");
  const [showSortOptions, setShowSortOptions] = useState(false);

  /* Löschen */
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const deleteConfirmRef = useRef(null);

  /* Neue Gruppe erstellen */
  const [newGroupName, setNewGroupName] = useState("");
  const [showNewGroupModal, setShowNewGroupModal] = useState(false);
  const [dashboardSearch, setDashboardSearch] = useState("");
  const [dashboardSuggestions, setDashboardSuggestions] = useState([]);
  const [showDashboardSuggestions, setShowDashboardSuggestions] = useState(false);
  const [selectedDashboards, setSelectedDashboards] = useState([]);
  const [addingDashboards, setAddingDashboards] = useState(false);
  const [showAddDashboardsTooltip, setShowAddDashboardsTooltip] = useState(false);
  const newGroupModalRef = useRef(null);
  const dashboardSuggestionsRef = useRef(null);
  const [highlightedDashboardIndex, setHighlightedDashboardIndex] = useState(-1);
  const [reloadKey, setReloadKey] = useState(0);

  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState('success');
  const showStatus = (message, type = 'success') => {
    setStatusType(type);
    setStatusMessage(message);
    setTimeout(() => setStatusMessage(""), 4000);
  };

  const { authToken: token } = useAuth();
  const navigate = useNavigate();

  const closeSortDropdown = useCallback(() => setShowSortOptions(false), []);
  useDropdownClose(showSortOptions, closeSortDropdown, '.groups-sort-wrapper');

  useFocusTrap(showDeleteConfirm, deleteConfirmRef);
  useFocusTrap(showNewGroupModal, newGroupModalRef);

  useNavigationStatus(showStatus);

  useEffect(() => {
    async function loadGroups() {
      try {
        setLoading(true);
        setError("");

        const sortMap = {
          name: { property: "name", order: "ASC" },
          created: { property: "createdTime", order: "DESC" },
          count: { property: "createdTime", order: "DESC" }
        };

        const sort = sortMap[sortOption];

        const params = new URLSearchParams({
          pageSize: PAGE_SIZE,
          page,
          sortProperty: sort.property,
          sortOrder: sort.order,
          includeShared: false
        });

        if (searchTerm) {
          params.append("textSearch", searchTerm);
        }

        const res = await fetch(
          `${API_BASE}/entityGroups/DASHBOARD?${params.toString()}`,
          { headers: authHeaders(token) }
        );

        if (!res.ok) {
          const errData = await res.json().catch(() => null);
          throw new Error(entityGroupError(
            res,
            errData?.detail || errData?.message || `Fehler beim Laden der Dashboard-Gruppen (HTTP ${res.status})`
          ));
        }
        const data = await res.json();

        const normalizedGroups = (data.data || [])
          .filter(g => g.name !== "All")
          .map(g => ({
            id: g.id.id,
            name: g.name,
            createdTime: g.createdTime,
            dashboardCount: 0
          }));

        // Dashboard-Anzahl pro Gruppe parallel laden
        const groupsWithCounts = await Promise.all(
          normalizedGroups.map(async grp => {
            try {
              const resp = await fetch(
                `${API_BASE}/entityGroup/${grp.id}/entities?pageSize=1&page=0`,
                { headers: authHeaders(token) }
              );
              if (!resp.ok) throw new Error();
              const cntData = await resp.json();
              return { ...grp, dashboardCount: cntData.totalElements ?? 0 };
            } catch {
              return { ...grp, dashboardCount: 0 };
            }
          })
        );

        const visibleCount = groupsWithCounts.length;

        setGroups(groupsWithCounts);
        setGroupCount(visibleCount);
        setTotalPages(Math.ceil(visibleCount / PAGE_SIZE));
      } catch (err) {
        setError(err.message || "Fehler beim Laden der Dashboard-Gruppen. Bitte prüfen Sie Ihre Netzwerkverbindung.");
        setGroups([]);
        setGroupCount(0);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    }

    if (token) loadGroups();
  }, [token, page, sortOption, searchTerm, reloadKey]);

  const suggestions = generateSuggestions(groups, searchTerm);

  /* ===============================
     Dashboard-Suche im Neues-Gruppe-Modal
  =============================== */
  /* Dashboard-Suche im Neues-Gruppe-Modal */
  useEffect(() => {
    if (!token) { setDashboardSuggestions([]); return; }
    const controller = new AbortController();
    async function fetchDashboards() {
      try {
        const params = new URLSearchParams({ pageSize: 10, page: 0 });
        if (dashboardSearch) params.set('textSearch', dashboardSearch);
        const res = await fetch(
          `${API_BASE}/dashboards/all?${params.toString()}`,
          { headers: authHeaders(token), signal: controller.signal }
        );
        if (!res.ok) return;
        const data = await res.json();
        const selectedIds = new Set(selectedDashboards.map(d => d.id));
        setDashboardSuggestions(
          (data.data || []).filter(d => !selectedIds.has(d.id.id))
        );
      } catch {}
    }
    fetchDashboards();
    return () => controller.abort();
  }, [dashboardSearch, selectedDashboards, token]);

  function addDashboard(dashboard) {
    setSelectedDashboards(prev => [
      ...prev,
      { id: dashboard.id.id, name: dashboard.name || dashboard.title }
    ]);
    setDashboardSearch("");
    setDashboardSuggestions([]);
    setHighlightedDashboardIndex(-1);
    setShowDashboardSuggestions(false);
  }

  function removeSelectedDashboard(id) {
    setSelectedDashboards(prev => prev.filter(d => d.id !== id));
  }

  async function createNewGroup() {
    if (!newGroupName.trim()) {
      showStatus("Bitte geben Sie einen Namen für die Gruppe ein.", 'error');
      return;
    }

    try {
      setAddingDashboards(true);

      // 1. Neue Dashboard-Gruppe erstellen
      const createRes = await fetch(
        `${API_BASE}/entityGroup`,
        {
          method: "POST",
          headers: authHeaders(token),
          body: JSON.stringify({ name: newGroupName.trim(), type: "DASHBOARD" })
        }
      );
      if (!createRes.ok) {
        const errData = await createRes.json().catch(() => null);
        throw new Error(errData?.detail || errData?.message || `Gruppe konnte nicht erstellt werden (HTTP ${createRes.status})`);
      }
      const groupData = await createRes.json();
      const newGroupId = groupData.id.id;

      // 2. Dashboards zur Gruppe hinzufügen (optional)
      if (selectedDashboards.length > 0) {
        const dashboardIds = selectedDashboards.map(d => d.id);
        const addRes = await fetch(
          `${API_BASE}/entityGroup/${newGroupId}/addEntities`,
          {
            method: "POST",
            headers: authHeaders(token),
            body: JSON.stringify(dashboardIds)
          }
        );
        if (!addRes.ok) {
          const errData = await addRes.json().catch(() => null);
          throw new Error(errData?.detail || errData?.message || `Dashboards konnten nicht hinzugefügt werden (HTTP ${addRes.status})`);
        }
      }

      showStatus(`Gruppe \u201E${newGroupName}\u201C wurde erfolgreich erstellt.`);
      setShowNewGroupModal(false);
      setNewGroupName("");
      setSelectedDashboards([]);
      setDashboardSearch("");
      setDashboardSuggestions([]);
      setHighlightedDashboardIndex(-1);
      setPage(0);
      setReloadKey(k => k + 1);
    } catch (e) {
      console.error(e);
      showStatus(`Gruppe konnte nicht erstellt werden: ${e.message || 'Netzwerkfehler'}.`, 'error');
    } finally {
      setAddingDashboards(false);
    }
  }

  /* ===============================
     Gruppe löschen
  =============================== */
  async function deleteGroup() {
    if (!groupToDelete) return;

    try {
      setDeleting(true);

      const res = await fetch(
        `${API_BASE}/entityGroup/${groupToDelete.id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.detail || errData?.message || `Gruppe konnte nicht gelöscht werden (HTTP ${res.status})`);
      }
      setGroups(prev => prev.filter(g => g.id !== groupToDelete.id));
      setGroupCount(c => Math.max(0, c - 1));

      showStatus(`Gruppe „${groupToDelete.name}" wurde erfolgreich gelöscht.`);
    } catch (e) {
      console.error(e);
      showStatus(`Gruppe konnte nicht gelöscht werden: ${e.message || 'Netzwerkfehler'}.`, 'error');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
      setGroupToDelete(null);
    }
  }

  /* ===============================
     Render
  =============================== */
  return (
    <div>
      {/* Toolbar */}
      <div className="groups-toolbar">
        <div className="groups-toolbar-left">
          <h1 className="groups-title">
            Alle Dashboard-Gruppen ({groupCount ?? "..."})
          </h1>

          <div className="groups-sort-wrapper">
            <button
              className={`groups-sort-button ${showSortOptions ? "active" : ""}`}
              onClick={() => setShowSortOptions(p => !p)}
              aria-haspopup="listbox"
              aria-expanded={showSortOptions}
            >
              <Sortieren /> Sortieren
            </button>

            {showSortOptions && (
              <div className="groups-sort-dropdown" role="listbox">
                {[
                  { key: "name", label: "Name" },
                  { key: "created", label: "Hinzugefügt" },
                  { key: "count", label: "Anzahl Dashboards" }
                ].map(opt => (
                  <div
                    key={opt.key}
                    className={`groups-sort-option ${sortOption === opt.key ? "active" : ""}`}
                    role="option"
                    aria-selected={sortOption === opt.key}
                    tabIndex={0}
                    onClick={() => {
                      setSortOption(opt.key);
                      setPage(0);
                      setShowSortOptions(false);
                      searchInputRef.current?.focus();
                    }}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        setSortOption(opt.key);
                        setPage(0);
                        setShowSortOptions(false);
                        searchInputRef.current?.focus();
                      }
                    }}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Suche */}
        <div className="groups-toolbar-right">
          <button
            className="export-button"
            aria-label="Neue Dashboard-Gruppe erstellen"
            title="Neue Dashboard-Gruppe erstellen"
            onClick={() => setShowNewGroupModal(true)}
          >
            <div className="export-icon"><Hinzufügen /></div>
            <div>Gruppe erstellen</div>
          </button>

          <div className="groups-search-wrapper">
            <label htmlFor="dashboard-group-search" className="groups-search-label">
              Gruppe suchen
            </label>
            <span className="groups-search-icon"><Suche /></span>

            <input
              id="dashboard-group-search"
              className="groups-search-input"
              ref={searchInputRef}
              value={searchTerm}
              autoComplete="off"
              aria-autocomplete="list"
              aria-controls="dashboard-group-suggestions"
              aria-activedescendant={
                highlightedSuggestion >= 0 && showSuggestions
                  ? `dashboard-group-suggestion-${highlightedSuggestion}`
                  : undefined
              }
              onChange={e => {
                setSearchTerm(e.target.value);
                setPage(0);
                setShowSuggestions(true);
                setHighlightedSuggestion(-1);
              }}
              onFocus={() => setShowSuggestions(true)}
              onKeyDown={e => {
                if (!showSuggestions || suggestions.length === 0) return;

                if (e.key === "ArrowDown") {
                  setHighlightedSuggestion(h => Math.min(h + 1, suggestions.length - 1));
                  e.preventDefault();
                } else if (e.key === "ArrowUp") {
                  setHighlightedSuggestion(h => Math.max(h - 1, 0));
                  e.preventDefault();
                } else if (e.key === "Enter" && highlightedSuggestion >= 0) {
                  setSearchTerm(suggestions[highlightedSuggestion].name);
                  setShowSuggestions(false);
                  setHighlightedSuggestion(-1);
                  e.preventDefault();
                } else if (e.key === "Escape") {
                  setShowSuggestions(false);
                  setHighlightedSuggestion(-1);
                }
              }}
            />

            {searchTerm && (
              <button
                type="button"
                className="groups-search-clear"
                aria-label="Suche zurücksetzen"
                onClick={() => {
                  setSearchTerm("");
                  setShowSuggestions(false);
                  setHighlightedSuggestion(-1);
                  setPage(0);
                  searchInputRef.current?.focus();
                }}
              >
                ✖
              </button>
            )}

            {showSuggestions && suggestions.length > 0 && (
              <div
                id="dashboard-group-suggestions"
                className="groups-search-suggestions"
                role="listbox"
              >
                {suggestions.map((s, idx) => (
                  <div
                    key={s.id}
                    id={`dashboard-group-suggestion-${idx}`}
                    className={`groups-search-suggestion ${
                      highlightedSuggestion === idx ? "highlighted" : ""
                    }`}
                    role="option"
                    aria-selected={highlightedSuggestion === idx}
                    onMouseDown={() => {
                      setSearchTerm(s.name);
                      setShowSuggestions(false);
                      setHighlightedSuggestion(-1);
                    }}
                  >
                    {s.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {statusMessage && (
        <div className={`status-bar${statusType === 'error' ? ' status-bar--error' : ''}`} role="status" aria-live="polite">
          <p>{statusMessage}</p>
        </div>
      )}

      {/* Liste */}
      <div className="groups-table-container">
        {loading ? (
          <p>Dashboard-Gruppen werden geladen…</p>
        ) : error ? (
          <div className="error-message" role="alert">
            {error}
          </div>
        ) : (
          <>
            <div className="groups-list">
              <div className="groups-row groups-header">
                <div>Gruppenname</div>
                <div>Anzahl Dashboards</div>
                <div>Hinzugefügt</div>
                <div>Öffnen</div>
                <div>Löschen</div>
              </div>

              {(!groups || groups.length === 0) ? (
                <div className="no-items" style={{ padding: '12px 20px', color: '#474747' }}>Keine Dashboard-Gruppen gefunden</div>
              ) : groups.map(g => (
                <div key={g.id} className="groups-row groups-item clickable-row"
                  onClick={(e) => {
                    if (e.target.closest('button, .sort-wrapper')) return;
                    navigate(`/dashboard-groups/${g.id}`, { state: { name: g.name } });
                  }}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && !e.target.closest('button, .sort-wrapper')) {
                      e.preventDefault();
                      navigate(`/dashboard-groups/${g.id}`, { state: { name: g.name } });
                    }
                  }}
                  tabIndex={0}
                  role="listitem"
                  aria-label={`Gruppe ${g.name} öffnen`}
                >
                  <div>{g.name}</div>
                  <div>{g.dashboardCount}</div>
                  <div>{formatDateDE(g.createdTime)}</div>
                  <div className="cell-right">
                    <button
                      className="icon-button open"
                      aria-label={`Gruppe ${g.name} öffnen`}
                      title={`Gruppe ${g.name} öffnen`}
                      tabIndex={0}
                      onClick={() => navigate(`/dashboard-groups/${g.id}`, { state: { name: g.name } })}
                    >
                      <Öffnen />
                    </button>
                  </div>
                  <div className="cell-right">
                    <button
                      className="icon-button delete"
                      aria-label={`Gruppe ${g.name} löschen`}
                      title={`Gruppe ${g.name} löschen`}
                      onClick={() => {
                        setGroupToDelete({ id: g.id, name: g.name });
                        setShowDeleteConfirm(true);
                      }}
                    >
                      <Löschen />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {groupCount > 0 && (
              <Pagination page={page} totalPages={totalPages} totalItems={groupCount} pageSize={PAGE_SIZE} onPageChange={setPage} label="Gruppen" />
            )}
          </>
        )}
      </div>

      {showDeleteConfirm && groupToDelete && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="delete-confirm-title">
          <div className="modal" ref={deleteConfirmRef}>
            <h2 id="delete-confirm-title">
              Sind Sie sicher, dass Sie die Gruppe <br/>
              <i>{groupToDelete.name}</i> löschen möchten?
            </h2>

            <p>
              Nach der Bestätigung wird die Gruppe und alle zugehörigen Daten <strong>unwiderruflich</strong> gelöscht.
            </p>

            <div className="modal-actions">
              <button
                className="modal-primary"
                onClick={deleteGroup}
                disabled={deleting}
              >
                {deleting ? (
                    "Löschen…"
                  ) : (
                    <>
                      <Löschen />
                      Löschen bestätigen
                    </>
                  )}
              </button>

              <button
                className="modal-secondary"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setGroupToDelete(null);
                }}
                disabled={deleting}
              >
                <Abbrechen /> Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {showNewGroupModal && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="new-dashboard-group-title">
          <div className="modal large" ref={newGroupModalRef}>

            <h2 id="new-dashboard-group-title">Neue Dashboard-Gruppe erstellen</h2>

            <h3 className="step-title"> 1. Name der Gruppe</h3>
            <div className="modal-device-search-wrapper">
              <label htmlFor="new-dashboard-group-name" className="modal-device-search-label">
                Name<span className="required">*</span>
              </label>
              <input
                id="new-dashboard-group-name"
                className="modal-device-search-input"
                value={newGroupName}
                placeholder="Name eingeben"
                autoFocus
                onChange={e => setNewGroupName(e.target.value)}
                maxLength="50"
                required
                aria-required="true"
                aria-label="Name der Gruppe eingeben"
              />
            </div>

            <p className="required-hint">* Pflichtfeld</p>

            <h3 className="step-title"> 2. Dashboards hinzufügen
              <button
                type="button"
                className={`tooltip-icon-button ${showAddDashboardsTooltip ? "active" : ""}`}
                aria-label="Informationen zum Hinzufügen von Dashboards"
                aria-expanded={showAddDashboardsTooltip}
                aria-controls="add-dashboards-tooltip"
                onClick={() => setShowAddDashboardsTooltip(prev => !prev)}
                onKeyDown={e => { if (e.key === "Escape") setShowAddDashboardsTooltip(false); }}
              >
                <Hinweis />
              </button>
            </h3>

            {showAddDashboardsTooltip && (
              <div id="add-dashboards-tooltip" role="tooltip" className="custom-tooltip">
                Sie können direkt Dashboards zu der neuen Gruppe hinzufügen.
                Suchen Sie dafür nach vorhandenen Dashboards über die Suchfunktion.
                Dieser Schritt ist nicht verpflichtend und kann auch später in den Gruppendetails durchgeführt werden.
              </div>
            )}

            <div className="modal-device-search-wrapper">
              <label htmlFor="dashboard-group-modal-search" className="modal-device-search-label">
                Dashboard suchen
              </label>
              <span className="modal-device-search-icon">
                <Suche />
              </span>
              <input
                id="dashboard-group-modal-search"
                className="modal-device-search-input"
                value={dashboardSearch}
                onChange={e => {
                  setDashboardSearch(e.target.value);
                  setHighlightedDashboardIndex(-1);
                }}
                onFocus={() => setShowDashboardSuggestions(true)}
                onBlur={() => setTimeout(() => setShowDashboardSuggestions(false), 150)}
                onKeyDown={e => {
                  if (e.key === "Escape") {
                    if (showDashboardSuggestions && dashboardSuggestions.length > 0) {
                      setShowDashboardSuggestions(false);
                      setHighlightedDashboardIndex(-1);
                    } else {
                      setShowNewGroupModal(false);
                    }
                    return;
                  }
                  if (!showDashboardSuggestions || dashboardSuggestions.length === 0) return;
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setHighlightedDashboardIndex(prev => Math.min(prev + 1, dashboardSuggestions.length - 1));
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setHighlightedDashboardIndex(prev => Math.max(prev - 1, -1));
                  } else if (e.key === "Enter" && highlightedDashboardIndex >= 0) {
                    e.preventDefault();
                    addDashboard(dashboardSuggestions[highlightedDashboardIndex]);
                  } else if (e.key === "Escape") {
                    setHighlightedDashboardIndex(-1);
                  }
                }}
                placeholder="Dashboard suchen"
                aria-autocomplete="list"
                aria-controls="dashboard-suggestions"
                aria-activedescendant={
                  highlightedDashboardIndex >= 0 && dashboardSuggestions.length > 0
                    ? `dashboard-suggestion-${highlightedDashboardIndex}`
                    : undefined
                }
              />

              {dashboardSearch && (
                <button
                  className="modal-device-search-clear"
                  onClick={() => {
                    setDashboardSearch("");
                    setHighlightedDashboardIndex(-1);
                  }}
                  aria-label="Eingabe zurücksetzen"
                  title="Eingabe zurücksetzen"
                >
                  <Abbrechen />
                </button>
              )}

              {showDashboardSuggestions && dashboardSuggestions.length > 0 && (
                <div
                  id="dashboard-suggestions"
                  className="modal-device-search-suggestions"
                  role="listbox"
                  ref={dashboardSuggestionsRef}
                >
                  {dashboardSuggestions.map((d, idx) => (
                    <div
                      key={d.id.id}
                      id={`dashboard-suggestion-${idx}`}
                      className={`modal-device-search-suggestion ${
                        highlightedDashboardIndex === idx ? "highlighted" : ""
                      }`}
                      role="option"
                      aria-selected={highlightedDashboardIndex === idx}
                      onMouseDown={() => addDashboard(d)}
                      onMouseEnter={() => setHighlightedDashboardIndex(idx)}
                    >
                      {d.name || d.title}
                    </div>
                  ))}
                </div>
              )}
              {showDashboardSuggestions && dashboardSuggestions.length === 0 && dashboardSearch && (
                <div
                  id="dashboard-suggestions"
                  className="modal-device-search-suggestions"
                  role="listbox"
                  ref={dashboardSuggestionsRef}
                >
                  <div className="modal-device-search-suggestion-no-results">Keine passenden Dashboards gefunden</div>
                </div>
              )}
            </div>

            {selectedDashboards.length > 0 && (
              <div className="selected-badges">
                {selectedDashboards.map(d => (
                  <span key={d.id} className="selected-badge">
                    {d.name}
                    <button
                      type="button"
                      className="badge-remove"
                      onClick={() => removeSelectedDashboard(d.id)}
                      aria-label="Dashboard entfernen"
                      title="Dashboard entfernen"
                    >
                      <Abbrechen />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="modal-actions">
              <button
                className="modal-primary-positive"
                onClick={createNewGroup}
                disabled={addingDashboards || newGroupName.trim() === ""}
              >
                {addingDashboards ? (
                  "Gruppe wird erstellt…"
                ) : (
                  <>
                    <Bestätigen />
                    {" "}Gruppe erstellen
                  </>
                )}
              </button>
              <button
                className="modal-secondary"
                onClick={() => {
                  setShowNewGroupModal(false);
                  setSelectedDashboards([]);
                  setDashboardSearch("");
                  setHighlightedDashboardIndex(-1);
                  setNewGroupName("");
                }}
                disabled={addingDashboards}
              >
                <Abbrechen /> Abbrechen
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardGroups;
